import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  ClickAwayListener,
  Paper,
  Divider,
  Link as MuiLink,
} from "@mui/material";
import NeoLinkIcon from "../../assets/img/theo_link.svg";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import { formatTimestamp, cleanContent } from "../../utils/chatUtils";
import { normalizeMessages } from "../utils/messageUtils";
import {
  agentDisplayNameForMessage,
  getChatBubblePresentation,
  isLoggedInHumanMessage,
  isNeoAssistantAuthor,
} from "../utils/messageSenderLabels";
import NeoButton from "./NeoButton";
import ChatBubble from "./ChatBubble";
import {
  buildReportHref,
  extractResearchJobId,
  ResearchReportJobStatusInline,
} from "./ResearchReportJobStatusInline";
import {
  parseReportAttachmentsFromContent,
  stripReportAttachmentTokensForDisplay,
} from "../utils/reportAttachmentTokens";
import { TypingIndicator } from "./TypingIndicator";
import DeleteModal from "./DeleteModal";
import RemoteSessionInputSection from "../../components/RemoteSessionInputSection";
import type { ChatSendPayload, QueuedChatMessage } from "../../components/InputBox";
import useComputerUseChatDisabledReason from "../../hooks/useComputerUseChatDisabledReason";
import useThreadWait, { composeChatDisabledReason } from "../../hooks/useThreadWait";
import useChatReplyPlaceholder from "../../hooks/useChatReplyPlaceholder";
import useComputerUseRuns from "../../hooks/useComputerUseRuns";
import useTicketResolverState from "../../hooks/useTicketResolverState";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { useUserMsp } from "../../hooks/useUserMsp";
import { canAccessAnalyticsAssistant } from "../../utils/analyticsAssistantAccess";
import { BEFORE_FIRST_MESSAGE } from "../../utils/computerUseRunPlacement";
import { RESOLVER_STATE_COLORS, RESOLVER_STATE_LABELS, STATE_DOT_SIZE } from "./resolverStateStyle";

interface Message {
  message_id: string;
  ai_thread_id: string;
  user_id: string;
  content: string;
  content_type?: string;
  created_at: { seconds: string; nanos: number };
  pending?: boolean;
  isStreaming?: boolean;
  status?: 'pending' | 'sent' | 'confirmed' | 'failed';
  error?: string;
  serverMessageId?: string;
  requestId?: string;
  human_review?: {
    tool_name?: any;
    tool_description?: any;
    args_json?: any;
    steps_taken?: string[];
    arg_types_json?: any;
    plan_name?: string | string[];
    plan_description?: string | string[];
    plan_steps?: string[] | string[][];
    plan_url?: string | string[];
    information_request?: Array<{
      id: string;
      question: string;
    }>;
    information_answers?: Record<string, string>;
  };
  human_review_status?: string;
  human_review_response?: string;
  isHistory?: boolean;
  plan_progress?: {
    plan_description?: string;
    completed_step_indices: number[];
    total_steps: number;
  };
}

interface NeoAssistantProps {
  ticketId: string | null;
  ticketDisplayId: string | null;
  userId: string;
  currentUser: any;
  customerId?: string | null;
  aiThreadId?: string | null;
  initialMessages?: any[];
  // Extension-specific props
  isExtension?: boolean;
  onSendMessage?: (message: string, options?: { attachments?: ChatSendPayload["attachments"] }) => void;
  onStopTurn?: () => void;
  onReviewAction?: (
    action: "CONTINUE" | "CANCEL" | "UPDATE" | "SCHEDULED",
    args?: string,
    messageId?: string
  ) => void;
  messages?: Message[];
  chatIsLoading?: boolean;
  queuedMessages?: QueuedChatMessage[];
  onDeleteQueuedMessage?: (id: string) => void;
  onUpdateQueuedMessage?: (id: string, content: string) => void;
  onSendQueuedMessageNow?: (id: string) => void;
  canSendQueuedMessagesNow?: boolean;
  pills?: any[];
  pillsKeyword?: string;
  onPillsKeywordChange?: (kw: string) => void;
  filteredPills?: any[];
  selectedPill?: string | null;
  onSelectPill?: (id: string | null) => void;
  onClearChat?: () => void;
  onClose?: () => void;
  hideHeader?: boolean;
  reverseMessages?: boolean;
  showDeleteSnackbar?: boolean;
  deleteError?: string | null;
  onCloseSnackbar?: () => void;
  snackbarSuccessMessage?: string;
  snackbarErrorMessage?: string;
  onDelete?: () => Promise<any>;
  onChevron?: () => void;
  showChevron?: boolean;
  showDelete?: boolean;
  chevronIcon?: React.ReactNode;
  deleteIcon?: React.ReactNode;
  sx?: any;
  className?: string;
  headerSx?: any;
  headerClassName?: string;
  messagesSx?: any;
  messagesClassName?: string;
  inputSx?: any;
  inputClassName?: string;
  inputMessage?: string;
  setInputMessage?: (msg: string) => void;
  currentMspId?: string | number | null;
}

interface PillCategory {
  id: string;
  label: string;
  type: "application" | "platform";
  suggestions: string[];
}


export function TicketResolverStateChip({
  resolverState,
  resolverContext,
}: {
  resolverState: string | null | undefined;
  resolverContext?: { on?: "technician" | "system"; kind?: string; reason?: string } | null;
}) {
  if (!resolverState || resolverState === "cancelled" || !RESOLVER_STATE_LABELS[resolverState]) return null;
  const label =
    resolverState === "waiting" && resolverContext?.on
      ? `Waiting for ${resolverContext.on === "technician" ? "technician" : "system"}${resolverContext.kind === "escalation" ? " · escalated" : ""}`
      : RESOLVER_STATE_LABELS[resolverState];
  const colors = RESOLVER_STATE_COLORS[resolverState];
  return (
    <Box
      component="span"
      title={`Ticket automation state: ${label}`}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        mx: 1,
        color: colors.text,
        fontFamily: "var(--theo-font-sans)",
        fontSize: "12px",
        fontWeight: 500,
        lineHeight: 1.4,
        whiteSpace: "nowrap",
      }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: STATE_DOT_SIZE,
          height: STATE_DOT_SIZE,
          borderRadius: "50%",
          flexShrink: 0,
          backgroundColor: colors.dot,
        }}
      />
      {label}
    </Box>
  );
}

interface PillsBarProps {
  selectedPill: string | null;
  onSelect: (id: string) => void;
  pillCategories: PillCategory[];
}

interface PromptsListProps {
  suggestions: string[];
  onSelect: (text: string) => void;
  onClose: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
}

const PillsBar: React.FC<PillsBarProps> = ({
  selectedPill,
  onSelect,
  pillCategories,
}) => (
  <Box
    data-interactive="true"
    sx={{
      position: "relative",
      width: "100%",
      "&::after": {
        content: '""',
        position: "absolute",
        right: 0,
        top: 0,
        bottom: 0,
        width: "40px",
        background: "linear-gradient(to right, transparent, #fff)",
        pointerEvents: "none",
        zIndex: 1,
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        py: 1,
        px: 2,
        overflowX: "auto",
        whiteSpace: "nowrap",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        "-ms-overflow-style": "none",
        width: "100%",
        justifyContent: "flex-start",
        "& > *": {
          flexShrink: 0,
          width: {
            xs: "calc(100% - 1rem)",
            sm: "calc(50% - 1rem)",
            md: "calc((100% - 3rem) / 3)",
          },
          minWidth: {
            xs: "200px",
            sm: "160px",
            md: "120px",
          },
          maxWidth: {
            xs: "100%",
            sm: "240px",
            md: "200px",
          },
        },
      }}
    >
      {pillCategories.map((pill: PillCategory) => (
        <NeoButton
          key={pill.id}
          onClick={() => onSelect(pill.id)}
          variant={selectedPill === pill.id ? "primary" : "secondary"}
          sx={{
            borderRadius: "10px",
            background: selectedPill === pill.id ? "#E5E7EB" : "#F3F4F6",
            color: "#1a2b2e",
            fontWeight: 600,
            fontSize: {
              xs: "14px",
              sm: "15px",
              md: "16px",
            },
            px: {
              xs: 1.5,
              sm: 2,
            },
            py: 0.8,
            minWidth: "fit-content",
            boxShadow: "none",
            textTransform: "none",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            "&:hover": {
              background: "#E5E7EB",
              boxShadow: "none",
            },
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <Typography
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: "inherit",
            }}
          >
            {pill.label}
          </Typography>
        </NeoButton>
      ))}
    </Box>
  </Box>
);

const PromptsList: React.FC<PromptsListProps> = ({
  suggestions,
  onSelect,
  onClose,
}) => (
  <Box
    data-interactive="true"
    sx={{
      width: "100%",
      background: "#F5F5F5",
      border: "1px solid #E5E7EB",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      minWidth: 0,
      maxHeight: "400px",
    }}
  >
    {/* Header with close button */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2.5,
        py: 2,
        borderBottom: "1px solid #E5E7EB",
        background: "#F9FAFB",
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 700,
          fontSize: "18px",
          color: "#5A5E73",
          lineHeight: "20px",
          letterSpacing: "-0.01em",
        }}
      >
        Prompts
      </Typography>
      <IconButton
        aria-label="Close prompts"
        onClick={onClose}
        sx={{
          color: "#6B7280",
          "&:hover": {
            background: "#F3F4F6",
          },
        }}
      >
        <CloseIcon />
      </IconButton>
    </Box>

    {/* Suggestions list with custom scrollbar */}
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        background: "#F5F5F5",
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#414141",
          borderRadius: "999px",
          border: "2px solid #414141",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
          margin: "4px 0",
        },
        scrollbarWidth: "thin",
        scrollbarColor: "#414141 transparent",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          "& > *:not(:last-child)": {
            borderBottom: "1px solid #F3F4F6",
          },
        }}
      >
        {suggestions.map((s, idx) => (
          <Box
            key={idx}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2.5,
              py: 2,
              minHeight: 48,
              cursor: "pointer",
              background: "#FFFFFF",
              transition: "all 0.15s ease",
              "&:hover": {
                background: "#F9FAFB",
              },
              "&:active": {
                background: "#F3F4F6",
              },
            }}
            onClick={() => onSelect(s)}
          >
            <SearchIcon
              sx={{
                color: "#9CA3AF",
                fontSize: 16,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: "14px",
                color: "#4B5563",
                fontWeight: 450,
                fontFamily: "Inter, sans-serif",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                lineHeight: "20px",
                letterSpacing: "-0.01em",
              }}
            >
              {s}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

const InputBox: React.FC<{
  inputMessage: string;
  setInputMessage: (msg: string) => void;
  chatIsLoading: boolean;
  aiThreadId: string | null;
  userId?: string | null;
  handleSendMessage: (messageOverride?: string | ChatSendPayload) => void;
  queuedMessages?: QueuedChatMessage[];
  onDeleteQueuedMessage?: (id: string) => void;
  onUpdateQueuedMessage?: (id: string, content: string) => void;
  onSendQueuedMessageNow?: (id: string) => void;
  canSendQueuedMessagesNow?: boolean;
  onStopTurn?: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  currentMspId?: string | number | null;
}> = ({
  inputMessage,
  setInputMessage,
  chatIsLoading,
  aiThreadId,
  userId = null,
  handleSendMessage,
  queuedMessages = [],
  onDeleteQueuedMessage,
  onUpdateQueuedMessage,
  onSendQueuedMessageNow,
  canSendQueuedMessagesNow,
  onStopTurn,
  inputRef,
  currentMspId = null,
}) => {
  // A live browser run is answered with the review controls, never with a chat message.
  const computerUseChatDisabledReason = useComputerUseChatDisabledReason(aiThreadId);
  const threadWait = useThreadWait(aiThreadId);
  const chatDisabledReason = composeChatDisabledReason(threadWait, computerUseChatDisabledReason);
  // Theo's open question takes a chat reply: the composer stays open and says so.
  const chatReplyPlaceholder = useChatReplyPlaceholder(aiThreadId);
  return (
    <RemoteSessionInputSection
      inputMessage={inputMessage}
      setInputMessage={setInputMessage}
      chatIsLoading={chatIsLoading}
      aiThreadId={aiThreadId}
      userId={userId}
      handleSendMessage={handleSendMessage}
      queuedMessages={queuedMessages}
      onDeleteQueuedMessage={onDeleteQueuedMessage}
      onUpdateQueuedMessage={onUpdateQueuedMessage}
      onSendQueuedMessageNow={onSendQueuedMessageNow}
      canSendQueuedMessagesNow={canSendQueuedMessagesNow}
      onStopTurn={onStopTurn}
      inputRef={inputRef}
      currentMspId={currentMspId}
      disabledReason={chatDisabledReason}
      placeholder={chatReplyPlaceholder}
    />
  );
};

const NeoAssistant: React.FC<NeoAssistantProps> = ({
  ticketId,
  ticketDisplayId,
  userId,
  currentUser,
  customerId,
  aiThreadId: propAiThreadId,
  initialMessages,
  isExtension = false,
  onSendMessage,
  onStopTurn,
  onReviewAction,
  messages = [],
  chatIsLoading = false,
  queuedMessages = [],
  onDeleteQueuedMessage,
  onUpdateQueuedMessage,
  onSendQueuedMessageNow,
  canSendQueuedMessagesNow,
  pills = [],
  pillsKeyword: pillsKeywordProp,
  onPillsKeywordChange,
  filteredPills: filteredPillsProp,
  selectedPill: selectedPillProp,
  onSelectPill,
  onClearChat,
  onClose,
  hideHeader = false,
  reverseMessages = false,
  showDeleteSnackbar: showDeleteSnackbarProp,
  deleteError: deleteErrorProp,
  onCloseSnackbar,
  snackbarSuccessMessage,
  snackbarErrorMessage,
  onDelete,
  onChevron,
  showChevron = false,
  showDelete = true,
  chevronIcon,
  deleteIcon,
  sx,
  className,
  headerSx,
  headerClassName,
  messagesSx,
  messagesClassName,
  inputSx,
  inputClassName,
  inputMessage: inputMessageProp,
  setInputMessage: setInputMessageProp,
  currentMspId = null,
}) => {
  const flags = useFeatureFlag();
  const { userMspInfo } = useUserMsp();
  const canUseDeepResearch = canAccessAnalyticsAssistant(flags as Record<string, unknown>, userMspInfo?.role);
  const ticketResolverState = useTicketResolverState(propAiThreadId, ticketId);
  const resolvedCurrentMspId =
    currentMspId ??
    currentUser?.msp_id ??
    currentUser?.mspId ??
    null;
  const [inputMessage, setInputMessage] = useState("");
  const [showPrompts, setShowPrompts] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [internalPillsKeyword] = useState("");
  const [internalSelectedPill, setInternalSelectedPill] = useState<
    string | null
  >(null);
  const [internalShowDeleteSnackbar, setInternalShowDeleteSnackbar] =
    useState(false);
  const [internalDeleteError, setInternalDeleteError] = useState<string | null>(
    null
  );

  const lastMessageForScroll =
    messages.length > 0 ? messages[messages.length - 1] : undefined;
  const lastMessageId = lastMessageForScroll?.message_id;
  const lastContentType = lastMessageForScroll?.content_type;
  const lastContentLength = (lastMessageForScroll?.content ?? "").length;

  useEffect(() => {
    const el = messagesEndRef.current;
    if (!el) return;
    const last = messages[messages.length - 1];
    const streaming = last?.user_id === "neo" && last?.isStreaming;
    requestAnimationFrame(() => {
      el.scrollIntoView({
        behavior: streaming ? "auto" : "smooth",
        block: "end",
      });
    });
  }, [
    messages.length,
    lastMessageId,
    lastContentType,
    lastContentLength,
    chatIsLoading,
    messages,
  ]);

  const handleSendMessage = (messageOverride?: string | ChatSendPayload) => {
    const payload =
      typeof messageOverride === "object" && messageOverride !== null
        ? messageOverride
        : { content: String(messageOverride ?? inputMessage) };
    const textToSend = String(payload.content || "").trim();
    const attachments = Array.isArray(payload.attachments) ? payload.attachments : [];
    if (!textToSend && attachments.length === 0) return;

    if (onSendMessage) {
      onSendMessage(textToSend, { attachments });
      setInputMessage("");
      setSelectedPill(null);
      setShowPrompts(false);
    }
  };

  const nullStatePrompts = [
    "Please handle this ticket",
  ];
  const handleNullStatePromptClick = (prompt: string) => {
    if (chatIsLoading || !onSendMessage) return;
    onSendMessage(prompt);
    setInputMessage("");
    setSelectedPill(null);
    setShowPrompts(false);
  };

  // Controlled/uncontrolled logic
  const selectedPill =
    selectedPillProp !== undefined ? selectedPillProp : internalSelectedPill;
  const setSelectedPill = onSelectPill || setInternalSelectedPill;
  const inputMsg =
    inputMessageProp !== undefined ? inputMessageProp : inputMessage;
  const setInputMsg = setInputMessageProp || setInputMessage;

  const handleChoiceOptionClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    const option = target?.closest?.("[data-neo-choice-option]") as HTMLElement | null;
    const choiceText = option?.getAttribute("data-neo-choice-option")?.trim();
    if (!choiceText) return;

    event.preventDefault();
    setInputMsg(choiceText);
    setShowPrompts(false);
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 0);
  };

  // PillsBar click logic
  const handlePillClick = (pillId: string) => {
    const pill = pills.find((p: any) => p.id === pillId);
    if (pill) {
      setInputMsg(pill.label);
      setSelectedPill(pill.id);
      setShowPrompts(true);
      setTimeout(() => {
        if (inputRef.current) inputRef.current.blur();
      }, 0);
    }
  };

  // Prompts for selected pill
  const selectedPillObj = pills.find((p: any) => p.id === selectedPill);
  const promptSuggestions = selectedPillObj ? selectedPillObj.suggestions : [];

  // Prompt click logic
  const handleSuggestionClick = (text: string) => {
    setInputMsg(text);
    setShowPrompts(false);
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 0);
  };

  const handleClosePrompts = () => {
    setSelectedPill(null);
    setShowPrompts(false);
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 0);
  };

  const handleClearChat = async () => {
    if (onClearChat) {
      onClearChat();
      setShowDeleteModal(false);
      setInternalShowDeleteSnackbar(true);
      setInternalDeleteError(null);
    }
  };

  const handleReviewAction = async (
    action: "CONTINUE" | "CANCEL" | "UPDATE" | "SCHEDULED",
    args?: string,
    messageId?: string
  ) => {
    if (onReviewAction) {
      await onReviewAction(action, args, messageId);
    }
  };

  const pillsKeyword =
    pillsKeywordProp !== undefined ? pillsKeywordProp : internalPillsKeyword;
  const filteredPills =
    filteredPillsProp !== undefined
      ? filteredPillsProp
      : pillsKeyword
      ? pills.filter((pill: any) =>
          pill.label.toLowerCase().includes(pillsKeyword.toLowerCase())
        )
      : pills;

  // Message rendering logic
  const normalizedMessages = React.useMemo(
    () => normalizeMessages(Array.isArray(messages) ? messages : []) as unknown as Message[],
    [messages]
  );
  const renderedMessages = reverseMessages
    ? [...normalizedMessages].slice().reverse()
    : normalizedMessages;

  const renderRunsAt = useComputerUseRuns(propAiThreadId, renderedMessages);

  /**
   * Browser-run panels, or nothing when the transcript is drawn newest-first.
   *
   * Placement is by position among the messages as rendered, so reversed it would put a run
   * on the wrong side of the message that asked for it. Left out rather than shown somewhere
   * misleading; no caller reverses today.
   */
  const renderComputerUseRuns = reverseMessages ? () => null : renderRunsAt;

  const hideNullStatePrompts =
    chatIsLoading ||
    renderedMessages.some((m) => Boolean(m.isStreaming));

  const showDeleteSnackbar =
    showDeleteSnackbarProp !== undefined
      ? showDeleteSnackbarProp
      : internalShowDeleteSnackbar;
  const deleteError =
    deleteErrorProp !== undefined ? deleteErrorProp : internalDeleteError;
  const handleCloseSnackbar =
    onCloseSnackbar ||
    (() => {
      setInternalShowDeleteSnackbar(false);
      setInternalDeleteError(null);
    });

  // Render container based on extension flag
  if (isExtension) {
    return (
      <Box
        data-scrollable="true"
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          ...sx,
        }}
        className={className}
      >
        {/* Header */}
        {!hideHeader && (
          <Box
            data-drag-handle="true"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
              py: 1.5,
              px: 2,
              borderBottom: "1px solid #E5E7EB",
              background: "#fff",
              cursor: "move",
              ...headerSx,
            }}
            className={headerClassName}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <img
                src={NeoLinkIcon}
                alt="Neo"
                style={{ marginBottom: 8, width: 60, height: 40 }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 600,
                  color: "#5A5E73",
                  fontSize: 18,
                }}
              ></Typography>
              {ticketDisplayId && (
                <Box
                  sx={{
                    background: "#F7F8FA",
                    color: "#19213D",
                    fontWeight: 500,
                    fontSize: "12px",
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    fontFamily: "Inter",
                    ml: 2,
                    display: "flex",
                    alignItems: "center",
                    boxShadow: "0 1px 3px 0 rgba(16, 30, 54, 0.1)",
                  }}
                >
                  Ticket: {ticketDisplayId}
                </Box>
              )}
              <TicketResolverStateChip
                resolverState={ticketResolverState?.resolver_state}
                resolverContext={ticketResolverState?.resolver_context}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {showDelete && (
                <IconButton
                  size="small"
                  onClick={() => setShowDeleteModal(true)}
                  sx={{
                    backgroundColor: "#fef2f2",
                    borderRadius: "50%",
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0,
                    mr: 0.5,
                    border: "1px solid #fecaca",
                    "&:hover": {
                      backgroundColor: "#fee2e2",
                      borderColor: "#fca5a5",
                    },
                  }}
                  title="Clear chat"
                >
                  {deleteIcon || (
                    <DeleteOutlineIcon
                      sx={{ fontSize: 18, color: "#dc2626" }}
                    />
                  )}
                </IconButton>
              )}
              {showChevron && (
                <IconButton
                  size="small"
                  onClick={onChevron}
                  sx={{
                    backgroundColor: "#e0e0e0",
                    borderRadius: "50%",
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0,
                  }}
                >
                  {chevronIcon || <ChevronRightIcon sx={{ fontSize: 24 }} />}
                </IconButton>
              )}
            </Box>
          </Box>
        )}

        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            ...messagesSx,
          }}
          className={messagesClassName}
        >
          {(() => {
            if (renderedMessages.length === 0 && !hideNullStatePrompts) {
              return (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 320,
                    py: 4,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.25,
                      alignItems: "center",
                    }}
                  >
                    {nullStatePrompts.map((promptText) => (
                      <Box
                        key={promptText}
                        component="button"
                        type="button"
                        onClick={() => handleNullStatePromptClick(promptText)}
                        disabled={chatIsLoading || !onSendMessage}
                        sx={{
                          border: "1px solid #E6EBF2",
                          borderRadius: "10px",
                          px: 2.5,
                          py: 1.25,
                          background: "#FBFCFE",
                          color: "#5E6B82",
                          fontFamily: "var(--theo-font-sans)",
                          fontSize: "14px",
                          fontWeight: 500,
                          cursor:
                            chatIsLoading || !onSendMessage
                              ? "not-allowed"
                              : "pointer",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            background:
                              chatIsLoading || !onSendMessage
                                ? "#FBFCFE"
                                : "#F5F8FC",
                            borderColor:
                              chatIsLoading || !onSendMessage
                                ? "#E6EBF2"
                                : "#DDE5F0",
                          },
                          "&:disabled": {
                            opacity: 0.6,
                          },
                        }}
                      >
                        {promptText}
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            }
            // Plan steps are now part of regular AI messages, no need to filter PLAN_MESSAGE
            return (
              <>
              {renderComputerUseRuns(BEFORE_FIRST_MESSAGE)}
              {renderedMessages.map((message, index) => {
              // Parse plan_progress from PLAN_PROGRESS_UPDATE content_type messages
              let planProgress = message.plan_progress;
              if (message.content_type === "PLAN_PROGRESS_UPDATE" && message.content) {
                try {
                  const parsed = JSON.parse(message.content);
                  planProgress = {
                    plan_description: parsed.plan_description || "",
                    completed_step_indices: parsed.completed_step_indices || [],
                    total_steps: parsed.total_steps || 0,
                  };
                } catch (e) {
                  console.error("Failed to parse plan_progress from message:", e);
                }
              }
              
              const entryId = (message as any).entry_id;
              const baseKey = entryId
                ? `entry-${entryId}`
                : `msg-${message.message_id || "none"}-${message.created_at?.seconds || 0}-${message.created_at?.nanos || 0}-${index}`;
              const messageKey = message.isStreaming
                ? `${baseKey}-${(message.content || "").length}`
                : baseKey;

              const bubblePres = getChatBubblePresentation(
                message.user_id,
                userId,
                currentUser,
                true,
                agentDisplayNameForMessage(renderedMessages, index),
              );
              const isNeo = isNeoAssistantAuthor(message.user_id);

              return (
                <React.Fragment key={messageKey}>
                <ChatBubble
                  sender={bubblePres.chatBubbleSender}
                  content={(() => {
                    const displayBody = isNeo
                      ? String(message.content ?? "")
                      : stripReportAttachmentTokensForDisplay(message.content);
                    const cleanedContent = cleanContent(displayBody);
                    const researchJobId = isNeo
                      ? extractResearchJobId(message.content)
                      : null;
                    return (
                      <div>
                        <div
                          onClick={handleChoiceOptionClick}
                          dangerouslySetInnerHTML={{
                            __html: cleanedContent,
                          }}
                        />
                        {canUseDeepResearch && researchJobId && propAiThreadId ? (
                          <ResearchReportJobStatusInline
                            jobId={researchJobId}
                            aiThreadId={propAiThreadId}
                            pollEnabled
                          />
                        ) : null}
                      </div>
                    );
                  })()}
                  headerInlineContent={
                    (() => {
                      const reportAttachments =
                        isLoggedInHumanMessage(message.user_id, userId)
                          ? parseReportAttachmentsFromContent(message.content)
                          : [];
                      if (reportAttachments.length === 0) return null;
                      return (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {reportAttachments.map((attachment) => (
                            <MuiLink
                              key={`${message.message_id || "msg"}-report-header-${attachment.jobId}`}
                              href={buildReportHref(attachment.jobId, null)}
                              target="_blank"
                              rel="noreferrer"
                              underline="none"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                border: "1px solid #D1D5DB",
                                backgroundColor: "#F9FAFB",
                                color: "#374151",
                                borderRadius: "999px",
                                px: 1,
                                py: 0.2,
                                fontSize: 11,
                                fontWeight: 600,
                                fontFamily: "var(--theo-font-sans)",
                                lineHeight: 1.2,
                                maxWidth: 420,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                "&:hover": {
                                  backgroundColor: "#F3F4F6",
                                },
                              }}
                            >
                              {`report:${attachment.title}`}
                            </MuiLink>
                          ))}
                        </Box>
                      );
                    })()
                  }
                  timestamp={formatTimestamp(message.created_at?.seconds)}
                  senderName={bubblePres.senderName}
                  userAvatar={bubblePres.userAvatar}
                  userId={bubblePres.userIdLabel}
                  human_review={message.human_review}
                  human_review_status={message.human_review_status}
                  human_review_response={message.human_review_response}
                  isHistory={message.isHistory}
                  isLastMessage={index === renderedMessages.length - 1}
                  isExtension={isExtension}
                  onReviewAction={handleReviewAction}
                  onReviewError={(message) =>
                    setSnackbar({ open: true, message, severity: "error" })
                  }
                  messageId={message.message_id}
              currentUser={currentUser}
              aiThreadId={propAiThreadId || undefined}
              plan_progress={planProgress}
            />
            {/* A browser run renders where it happened: after the message that asked for
                it, before the reply that reports what it did. */}
            {renderComputerUseRuns(index)}
                </React.Fragment>
              );
            })}
              </>
            );
          })()}

          {chatIsLoading && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </Box>

        {/* PillsBar */}
        {pills.length > 0 && (
          <PillsBar
            selectedPill={selectedPill || null}
            onSelect={handlePillClick}
            pillCategories={filteredPills}
          />
        )}

        {/* Input Box */}
        <InputBox
          inputMessage={inputMsg}
          setInputMessage={setInputMsg}
          chatIsLoading={chatIsLoading}
          aiThreadId={propAiThreadId || null}
          userId={userId}
          handleSendMessage={handleSendMessage}
          queuedMessages={queuedMessages}
          onDeleteQueuedMessage={onDeleteQueuedMessage}
          onUpdateQueuedMessage={onUpdateQueuedMessage}
          onSendQueuedMessageNow={onSendQueuedMessageNow}
          canSendQueuedMessagesNow={canSendQueuedMessagesNow}
          onStopTurn={onStopTurn}
          inputRef={inputRef}
          currentMspId={resolvedCurrentMspId}
        />

        {/* Delete Modal */}
        <DeleteModal
          open={showDeleteModal}
          onCancel={() => setShowDeleteModal(false)}
          onDelete={onDelete || (async () => handleClearChat())}
          title="Clear Chat History?"
          description="Are you sure you want to clear all chat messages? This action cannot be undone."
          confirmText="Clear"
          cancelText="Cancel"
        />

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Delete Snackbar */}
        <Snackbar
          open={showDeleteSnackbar}
          autoHideDuration={1500}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={deleteError ? "error" : "success"}
            sx={{ width: "100%" }}
          >
            {deleteError
              ? snackbarErrorMessage || deleteError
              : snackbarSuccessMessage || "Chat history deleted successfully."}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // Client version with Paper container
  return (
    <Paper
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        border: "1px solid #E5E5E5",
        borderRadius: 3,
        boxShadow: "none",
        height: "calc(100vh - 64px - 24px - 24px)",
        maxHeight: "calc(100vh - 64px - 24px - 24px)",
        background: "#fff",
        width: "100%",
        minWidth: 0,
        ...sx,
      }}
      className={className}
    >
      {/* Header */}
      {!hideHeader && (
        <Box>
          <Box
            data-drag-handle="true"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
              py: 1.5,
              px: 2,
              cursor: "move",
              ...headerSx,
            }}
            className={headerClassName}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <img
                src={NeoLinkIcon}
                alt="Neo Link"
                style={{ marginBottom: 8, width: 60, height: 40 }}
              />
              {/* Removed extra Theo text */}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {ticketDisplayId && (
                <Box
                  sx={{
                    background: "#F7F8FA",
                    color: "#19213D",
                    fontWeight: 500,
                    fontSize: "12px",
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    fontFamily: "Inter",
                    ml: 0,
                    display: "flex",
                    alignItems: "center",
                    boxShadow: "0 1px 3px 0 rgba(16, 30, 54, 0.1)",
                  }}
                >
                  Ticket: {ticketDisplayId}
                </Box>
              )}
              <TicketResolverStateChip
                resolverState={ticketResolverState?.resolver_state}
                resolverContext={ticketResolverState?.resolver_context}
              />
              {showDelete && (
                <IconButton
                  size="small"
                  onClick={() => setShowDeleteModal(true)}
                  sx={{
                    backgroundColor: "#fef2f2",
                    borderRadius: "50%",
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0,
                    mr: 0.5,
                    border: "1px solid #fecaca",
                    "&:hover": {
                      backgroundColor: "#fee2e2",
                      borderColor: "#fca5a5",
                    },
                  }}
                  title="Clear chat"
                >
                  {deleteIcon || (
                    <DeleteOutlineIcon
                      sx={{ fontSize: 18, color: "#dc2626" }}
                    />
                  )}
                </IconButton>
              )}
              {showChevron && (
                <IconButton
                  size="small"
                  onClick={onChevron}
                  sx={{
                    backgroundColor: "#e0e0e0",
                    borderRadius: "50%",
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0,
                  }}
                >
                  {chevronIcon || <ChevronRightIcon sx={{ fontSize: 24 }} />}
                </IconButton>
              )}
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />
        </Box>
      )}

      {/* Messages */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          pr: 1,
          fontFamily: "var(--theo-font-sans)",
          mb: 2,
          ...messagesSx,
        }}
        className={messagesClassName}
      >
        {renderedMessages.length === 0 && !hideNullStatePrompts && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 280,
              py: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                alignItems: "center",
              }}
            >
              {nullStatePrompts.map((promptText) => (
                <Box
                  key={promptText}
                  component="button"
                  type="button"
                  onClick={() => handleNullStatePromptClick(promptText)}
                  disabled={chatIsLoading || !onSendMessage}
                  sx={{
                    border: "1px solid #E6EBF2",
                    borderRadius: "10px",
                    px: 2.5,
                    py: 1.25,
                    background: "#FBFCFE",
                    color: "#5E6B82",
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: "14px",
                    fontWeight: 500,
                    cursor:
                      chatIsLoading || !onSendMessage
                        ? "not-allowed"
                        : "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      background:
                        chatIsLoading || !onSendMessage
                          ? "#FBFCFE"
                          : "#F5F8FC",
                      borderColor:
                        chatIsLoading || !onSendMessage
                          ? "#E6EBF2"
                          : "#DDE5F0",
                    },
                    "&:disabled": {
                      opacity: 0.6,
                    },
                  }}
                >
                  {promptText}
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {renderComputerUseRuns(BEFORE_FIRST_MESSAGE)}
        {renderedMessages.map((message, index) => {
          const bubblePres = getChatBubblePresentation(
            message.user_id,
            userId,
            currentUser,
            true,
            agentDisplayNameForMessage(renderedMessages, index),
          );
          const isNeo = isNeoAssistantAuthor(message.user_id);

          return (
          <React.Fragment
            key={
              (message as any).entry_id
                ? `entry-${(message as any).entry_id}`
                : `msg-${message.message_id || "none"}-${message.created_at?.seconds || 0}-${message.created_at?.nanos || 0}-${index}`
            }
          >
            {message.content_type === "HUMAN_REVIEW" ? (
              <ChatBubble
                sender={bubblePres.chatBubbleSender}
                content={(() => {
                  const cleanedContent = cleanContent(message.content);
                  return (
                    <div
                      onClick={handleChoiceOptionClick}
                      dangerouslySetInnerHTML={{
                        __html: cleanedContent,
                      }}
                    />
                  );
                })()}
                timestamp={formatTimestamp(message.created_at?.seconds)}
                senderName={bubblePres.senderName}
                userAvatar={bubblePres.userAvatar}
                userId={bubblePres.userIdLabel}
                human_review={message.human_review}
                human_review_status={message.human_review_status}
                isHistory={
                  (message.human_review_status === "approved" ||
                    message.human_review_status === "canceled" ||
                    message.human_review_status === "scheduled")
                    ? true
                    : index < renderedMessages.length - 1
                }
                isLastMessage={index === renderedMessages.length - 1}
                isExtension={isExtension}
                onReviewAction={handleReviewAction}
                onReviewError={(message) =>
                  setSnackbar({ open: true, message, severity: "error" })
                }
                currentUser={currentUser}
                aiThreadId={propAiThreadId || undefined}
                customerId={customerId}
                pending={message.pending}
                status={message.status || (message.pending ? 'pending' : undefined)}
              />
            ) : (
              <ChatBubble
                sender={bubblePres.chatBubbleSender}
                content={(() => {
                  const displayBody = isNeo
                    ? String(message.content ?? "")
                    : stripReportAttachmentTokensForDisplay(message.content);
                  const cleanedContent = cleanContent(displayBody);
                  const researchJobId = isNeo
                    ? extractResearchJobId(message.content)
                    : null;
                  return (
                    <div>
                      <div
                        onClick={handleChoiceOptionClick}
                        dangerouslySetInnerHTML={{
                          __html: cleanedContent,
                        }}
                      />
                      {canUseDeepResearch && researchJobId && propAiThreadId ? (
                        <ResearchReportJobStatusInline
                          jobId={researchJobId}
                          aiThreadId={propAiThreadId}
                          pollEnabled
                        />
                      ) : null}
                    </div>
                  );
                })()}
                headerInlineContent={
                  (() => {
                    const reportAttachments =
                      isLoggedInHumanMessage(message.user_id, userId)
                        ? parseReportAttachmentsFromContent(message.content)
                        : [];
                    if (reportAttachments.length === 0) return null;
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {reportAttachments.map((attachment) => (
                          <MuiLink
                            key={`${message.message_id || "msg"}-report-header-${attachment.jobId}`}
                            href={buildReportHref(attachment.jobId, null)}
                            target="_blank"
                            rel="noreferrer"
                            underline="none"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              border: "1px solid #D1D5DB",
                              backgroundColor: "#F9FAFB",
                              color: "#374151",
                              borderRadius: "999px",
                              px: 1,
                              py: 0.2,
                              fontSize: 11,
                              fontWeight: 600,
                              fontFamily: "var(--theo-font-sans)",
                              lineHeight: 1.2,
                              maxWidth: 420,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              "&:hover": {
                                backgroundColor: "#F3F4F6",
                              },
                            }}
                          >
                            {`report:${attachment.title}`}
                          </MuiLink>
                        ))}
                      </Box>
                    );
                  })()
                }
                timestamp={formatTimestamp(message.created_at?.seconds)}
                senderName={bubblePres.senderName}
                userAvatar={bubblePres.userAvatar}
                userId={bubblePres.userIdLabel}
                human_review={message.human_review}
                human_review_status={message.human_review_status}
                isHistory={message.isHistory}
                isLastMessage={index === renderedMessages.length - 1}
                isExtension={isExtension}
                onReviewAction={handleReviewAction}
                onReviewError={(message) =>
                  setSnackbar({ open: true, message, severity: "error" })
                }
                currentUser={currentUser}
                aiThreadId={propAiThreadId || undefined}
                ticketId={ticketId}
                pending={message.pending}
                status={message.status || (message.pending ? 'pending' : undefined)}
              />
            )}
            {/* A browser run renders where it happened: after the message that asked for
                it, before the reply that reports what it did. */}
            {renderComputerUseRuns(index)}
          </React.Fragment>
          );
        })}

        {chatIsLoading && (
          <Box sx={{ display: "flex", justifyContent: "left", my: 1, ml: 4 }}>
            <TypingIndicator />
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Section: Input, Pills, Prompts */}
      <Box
        sx={{
          width: "100%",
          p: 0,
          m: 0,
        }}
      >
        {/* PillsBar, always displayed above input */}
        {pills.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <PillsBar
              selectedPill={selectedPill || null}
              onSelect={handlePillClick}
              pillCategories={filteredPills}
            />
          </Box>
        )}

        {/* Input and Prompts Container */}
        <Box sx={{ position: "relative" }}>
          {/* PromptsList above input, tightly aligned, with click-away to close */}
          {showPrompts && selectedPill && promptSuggestions.length > 0 && (
            <ClickAwayListener onClickAway={handleClosePrompts}>
              <Box
                sx={{
                  position: "absolute",
                  bottom: "100%",
                  left: 0,
                  width: "100%",
                  zIndex: 20,
                }}
              >
                <PromptsList
                  suggestions={promptSuggestions}
                  onSelect={handleSuggestionClick}
                  onClose={handleClosePrompts}
                  inputRef={inputRef}
                />
              </Box>
            </ClickAwayListener>
          )}
          {/* Always show the input */}
          <InputBox
            inputMessage={inputMsg}
            setInputMessage={setInputMsg}
            chatIsLoading={chatIsLoading}
            aiThreadId={propAiThreadId || null}
            userId={userId}
            handleSendMessage={handleSendMessage}
            queuedMessages={queuedMessages}
            onDeleteQueuedMessage={onDeleteQueuedMessage}
            onUpdateQueuedMessage={onUpdateQueuedMessage}
            onSendQueuedMessageNow={onSendQueuedMessageNow}
            canSendQueuedMessagesNow={canSendQueuedMessagesNow}
            onStopTurn={onStopTurn}
            inputRef={inputRef}
            currentMspId={resolvedCurrentMspId}
          />
        </Box>
      </Box>

      {/* Delete Modal */}
      <DeleteModal
        open={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onDelete={onDelete || (async () => handleClearChat())}
        title="Clear Chat History?"
        description="Are you sure you want to clear all chat messages? This action cannot be undone."
        confirmText="Clear"
        cancelText="Cancel"
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Snackbar */}
      <Snackbar
        open={showDeleteSnackbar}
        autoHideDuration={1500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={deleteError ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {deleteError
            ? snackbarErrorMessage || deleteError
            : snackbarSuccessMessage || "Chat history deleted successfully."}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default NeoAssistant;
